import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { SearchResult } from '../../api/models/search-result';
import { SearchUrlParameters } from '../../shared/model/search-url-parameters';
import { SearchEventPublisher } from '../../shared/service/search-event-publisher.service';
import { SearchEventTypeEnum } from '../../shared/model/search-event-type-enum';
import { SORTING_OPTIONS } from '../../shared/options/sorting-options';
import { LocaleService } from '../../shared/locale/locale.service';
import { DEFAULT_LOCALE_GROUP } from '../../shared/ni-constants';
import { TranslateService } from '@ngx-translate/core';
import { SearchUtilityBarDropdownItem } from './search-utility-bar-dropdown-item';

const LOCALE_SEPARATOR = ' / ';

@Component({
  selector: 'app-search-utility-bar',
  templateUrl: './search-utility-bar.component.html',
  styleUrls: ['./search-utility-bar.component.scss'],
})
export class SearchUtilityBarComponent implements OnInit, OnChanges {
  @Input()
  searchResult?: SearchResult;

  @Input()
  searchParams?: SearchUrlParameters;

  sortByItems: SearchUtilityBarDropdownItem[] = [];
  pageSizeItems: SearchUtilityBarDropdownItem[] = [];
  languageItems: SearchUtilityBarDropdownItem[] = [];

  shouldHide = false;

  constructor(
    private searchEventPublisher: SearchEventPublisher,
    private localeService: LocaleService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.refreshDropdowns();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.searchResult) {
      this.shouldHide = this.searchResult?.searchResultList?.length === 0;
      this.refreshDropdowns();
    }
  }

  private refreshDropdowns() {
    this.sortByItems = this.getSortByItems();
    this.pageSizeItems = this.getPageSizeItems();
    this.translateService.get('languageCode').subscribe(displayLangCode => {
      let lang: string = this.localeService.getCurrentLocale();

      if (lang === null || lang === '' || lang === undefined) {
        lang = displayLangCode;
      }

      this.languageItems = this.getLanguageItems(lang);
    });
  }

  private getSortByItems(): SearchUtilityBarDropdownItem[] {
    const selectedForFacet = SORTING_OPTIONS.find(opt => opt.code === this.searchResult?.lastSelectedCategoryFacetCode && opt.showSortingSelector);
    const override = localStorage.getItem('utilityBarSortByOverride');

    if (selectedForFacet !== undefined) {
      return selectedForFacet.sortingFields.map(opt => {
        return {
          displayText: this.translateService.instant(opt.key),
          value: opt.field,
          isSelected: opt.field === override && override === this.searchParams?.sb,
        };
      });
    }

    return [];
  }

  private getPageSizeItems(): SearchUtilityBarDropdownItem[] {
    const override = localStorage.getItem('utilityBarViewSizeOverride');
    return [10, 20, 30].map(opt => {
      return {
        displayText: opt.toString(),
        value: opt,
        isSelected: opt.toString() === override && override === this.searchParams?.ps?.toString(),
      };
    });
  }

  private static getDisplayNameOfLocale(locale: string, displayLangCode: string): string {
    locale = locale.toUpperCase().split('-')[0];
    // @ts-ignore
    const displayNames = new Intl.DisplayNames([displayLangCode], { type: 'language' });
    return displayNames.of(locale)!.toLowerCase();
  }

  private static toLanguage(locale: string): string {
    return locale.split('-')[0];
  }

  private getLanguageItems(displayLangCode: string): SearchUtilityBarDropdownItem[] {
    let items: SearchUtilityBarDropdownItem[] = [];

    const currentLanguage = this.localeService.getCurrentLocale().toLowerCase().split('-')[0];
    const defaultLanguage = DEFAULT_LOCALE_GROUP.toLowerCase().split('-')[0];

    if (currentLanguage !== defaultLanguage) {
      const override = localStorage.getItem('utilityBarLanguageOverride');
      [
        {
          displayText:
            SearchUtilityBarComponent.getDisplayNameOfLocale(DEFAULT_LOCALE_GROUP, displayLangCode) +
            LOCALE_SEPARATOR +
            SearchUtilityBarComponent.getDisplayNameOfLocale(this.localeService.getCurrentLocale(), displayLangCode),
          value: SearchUtilityBarComponent.toLanguage(DEFAULT_LOCALE_GROUP),
        },
        {
          displayText: SearchUtilityBarComponent.getDisplayNameOfLocale(
            this.localeService.getCurrentLocale(),
            displayLangCode
          ),
          value: SearchUtilityBarComponent.toLanguage(this.localeService.getCurrentLocale()),
        },
      ].forEach(item => {
        const temp: SearchUtilityBarDropdownItem = {
          ...item,
          isSelected: item.value === override,
        };
        items.push(temp);
      });
    }

    return items;
  }

  changeSortBy(newSortBy: string): void {
    localStorage.setItem('utilityBarSortByOverride', newSortBy);
    if (this.searchParams) {
      this.searchParams.sb = newSortBy;
      this.searchEventPublisher.publishEvent(SearchEventTypeEnum.SORT_RESULT, this.searchParams);
    }
  }

  changeViewSize(newSize: number): void {
    localStorage.setItem('utilityBarViewSizeOverride', newSize.toString());
    if (this.searchParams) {
      this.searchParams.ps = newSize;
      this.searchParams.pg = 1;
      this.searchEventPublisher.publishEvent(SearchEventTypeEnum.CHANGE_PAGE, this.searchParams);
    }
  }

  changeLanguage(newLanguage: string): void {
    localStorage.setItem('utilityBarLanguageOverride', newLanguage);
    if (this.searchParams) {
      if (newLanguage !== SearchUtilityBarComponent.toLanguage(DEFAULT_LOCALE_GROUP)) {
        this.searchParams.scope = newLanguage;
      } else {
        this.searchParams.scope = undefined;
      }
      this.searchParams.pg = 1;
    }
    this.searchEventPublisher.publishEvent(SearchEventTypeEnum.FILTER, this.searchParams ?? {pg:1});
    this.refreshDropdowns();
  }
}
